import { memo, useCallback, useMemo } from 'react';

import { AssetModalBreadcrumbs } from '~/components/AssetModalV2/Header/components/AssetModalBreadcrumbs';
import { PublicAssetTitleBreadcrumbContent } from '~/components/AssetModalV2/Header/components/PublicAssetTitleBreadcrumbContent';
import { useAssetTitleBreadcrumbItem } from '~/components/AssetModalV2/hooks/useAssetTitleBreadcrumbItem';
import { usePublicAssetLocationBreadcrumbs } from '~/components/AssetModalV2/hooks/usePublicAssetLocationBreadcrumbs';
import { usePublicShowcaseHomeBreadcrumb } from '~/components/v2/components/showcase/hooks/usePublicShowcaseHomeBreadcrumb';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';
import { type LocationBoard } from '~/store/router/types';

/**
 * The asset modal's breadcrumbs on the collection PAGE body (an asset opened from the collection's
 * own gallery, not from a drilled board — that opens the board tree's modal instead): the
 * collection-home crumb, then the asset's location trail.
 */
export const PublicShowcaseAssetBreadcrumbs = memo(() => {
  const { onNavigateToBoard } = usePublicShowcaseContext();
  const { showcaseHomeBreadcrumb } = usePublicShowcaseHomeBreadcrumb();

  // Board crumbs can't currently appear here — an asset opened from the collection page body never
  // has a centralized board seeded (boards only exist behind the drill, which mounts the board
  // tree's own modal) — but wiring the collection tree's real drill action costs nothing and keeps
  // this honest if board items ever do surface.
  const handleBoardClick = useCallback(
    (board: LocationBoard) => {
      void onNavigateToBoard({ boardId: board.id, boardTitle: board.title });
    },
    [onNavigateToBoard],
  );

  const assetTitleContent = useMemo(() => <PublicAssetTitleBreadcrumbContent />, []);

  const { assetLocationBreadcrumbItems } = usePublicAssetLocationBreadcrumbs({
    onBoardClick: handleBoardClick,
    // The library is a private-app concept — no public surface renders it (the private
    // breadcrumbs keep it).
    excludeLibrary: true,
  });
  const { assetTitleBreadcrumbItem } = useAssetTitleBreadcrumbItem({ assetTitleContent });

  const breadcrumbItems = useMemo(
    () => [
      showcaseHomeBreadcrumb,
      ...assetLocationBreadcrumbItems,
      ...(assetTitleBreadcrumbItem ? [assetTitleBreadcrumbItem] : []),
    ],
    [assetLocationBreadcrumbItems, assetTitleBreadcrumbItem, showcaseHomeBreadcrumb],
  );

  return <AssetModalBreadcrumbs breadcrumbItems={breadcrumbItems} />;
});

PublicShowcaseAssetBreadcrumbs.displayName = 'PublicShowcaseAssetBreadcrumbs';
